import { Form, Formik, getIn } from 'formik';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import Moment from 'react-moment';
import * as Yup from 'yup';

import { useParams } from '@gatsbyjs/reach-router';
import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core/styles';

import Confirm from '../../../components/confirm';
import ConfirmInput from '../../../components/confirm-input';
import ConfirmSelect from '../../../components/confirm-select';
import SaveBackdrop from '../../../components/saveBackdrop';
import Screen from '../../../components/screen';
import SplashWait from '../../../components/splash-wait';
import { AppContext } from '../../../contexts/app-context';
import GlobalStyles from '../../../global-styles';
import API from '../../../utils/api';
import {
  apiReponseError, apiRequestError, canReadContractOrder, canWriteContractOrder,
  checkUserAuthenticated, formatDateForInput, getContractOrder, getErrorObject, goToRoutePage,
  isFormDataValid, isFormReferenceValid, openExternalLinkWithTarget, redirectRoutePage,
  setWindowTitle
} from '../../../utils/helper';
import {
  AccountTreeIcon, AddIcon, ChevronLeftIcon, ChevronRightIcon, ClearIcon, CopyTextIcon,
  DeleteIcon, EditIcon, MoreIcon, OpenInNewIcon, SearchIcon, SettingIcon, ContactOrderAccountOnIcon, ContactOrderAccountOffIcon, ContactOrderAccountDisabledIcon
} from '../../../utils/icons';
import {
  Alert, Box, Breadcrumbs, Button, Card, CardContent, CardHeader, Checkbox, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, FormControlLabel, FormGroup, FormHelperText, FormLabel,
  Grid, IconButton, Input, InputAdornment, InputLabel, Link, ListItemIcon, ListItemText, Menu,
  MenuItem, Select, Switch, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TableSortLabel, TextField, Tooltip, Typography
} from '../../../utils/material-ui-core';
import AccountSearch from './AccountSearch';
import AccountTree from './accountTree';

const defaultConfirmationDialogSetting = {
  title: "",
  content: "",
  yesText: "",
  noText: "CANCEL",
  open: false,
  callbackOnClose: () => { },
  callbackOnConfirm: (confirm: boolean) => { },
};

// Default form value
const formDefaultValue = {
  contractOrderKey: "00000000-0000-0000-0000-000000000000",
  createdBy: "",
  createdDate: null,
  lastModifiedBy: "",
  lastModifiedDateTime: "",
  contractOrderNumber: "",
  shortDescription: "",
  longDescription: "",
  effectiveDate: "",
  expirationDate: "",
  clientCompanyKey: "",
  clientCompanyCode: "",
  clientCompanyName: "",
  programs: [],
  programsDescription: "",
  accountsDescription: "",
  accountValidStatus: "",
  taxExempt: false,
  postingStatusId: 0,
  postingStatus: "",
  equipmentVendorId: 0,
  equipmentVendor: "",
  contractTypeId: 0,
  contractType: "",
  invoiceStatusId: 0,
  invoiceStatus: "",
  masterAgreementLevel: "",
  orderNumber: "",
  override: "",
  lockedIndicator: false,
  orderCount: 0,
  statusId: 1,
  status: "Draft",
  accountNumber: "",
  folderId: 0,
  folderName: "",
  clientCompanyShortName: "",
  expiredIn: ""
};

const formDefaultTouchValue = {
  contractOrderKey: false,
  lastModifiedBy: false,
  lastModifiedDate: false,
  contractOrderNumber: false,
  shortDescription: true,
  longDescription: true,
  effectiveDate: true,
  expirationDate: true,
  clientCompanyKey: true,
  clientCompanyCode: false,
  clientCompanyName: false,
  programsDescription: false,
  accountValidStatus: false,
  taxExempt: true,
  postingStatusId: true,
  postingStatus: false,
  equipmentVendorId: true,
  equipmentVendor: false,
  contractTypeId: true,
  contractType: false,
  invoiceStatusId: true,
  invoiceStatus: false,
  masterAgreementLevel: true,
  orderNumber: true,
  override: true,
  lockedIndicator: false,
  orderCount: false,
  statusId: false,
  status: false,
  folderId: false,
  clientCompanyShortName: true
};

// Form validation schema
const formValidationSchema = Yup.object().shape({
  shortDescription: Yup.string().trim().required("Short description is required.").max(100, "Short description can't be more than 100 characters."),
  longDescription: Yup.string().trim().max(250, "Long description can't be more than 250 characters."),
  clientCompanyKey: Yup.string().trim().required("Client company is required."),
  masterAgreementLevel: Yup.string().trim().required("Master agreement level is required.").max(3, "Master agreement level can't be more than 3 characters."),
  orderNumber: Yup.string().trim().required("Order number is required.").max(10, "Order number can't be more than 10 characters."),
  postingStatusId: Yup.number().required("Posting status is required.").moreThan(0, "Posting status  is required."),
  equipmentVendorId: Yup.number().required("Equipment vendor is required.").moreThan(0, "Equipment vendor is required."),
  contractTypeId: Yup.number().required("Contract type is required.").moreThan(0, "Contract type  is required."),
  invoiceStatusId: Yup.number().required("Invoice status is required.").moreThan(0, "Invoice status  is required."),
  effectiveDate: Yup.date().required("Effective date is required."),
  expirationDate: Yup.date().nullable().notRequired().min(Yup.ref("effectiveDate"), "Expiration date may be empty or it can be greater or equal to the effective date."),
  accountNumber: Yup.string().trim().max(100, "Account number can't be more than 100 characters."),
  accountPrefix: Yup.string().trim().max(10, "Account prefix can't be more than 10 characters."),
  override: Yup.string().trim().max(10, "Contract order suffix can't be more than 10 characters."),
  clientCompanyShortName: Yup.string().trim().required("Contract order prefix is required.").max(50, "Contract order prefix can't be more than 50 characters."),
  programs: Yup.array().of(
    Yup.object().shape({
      accounts: Yup.array().of(
        Yup.object().shape({
          accountNumber: Yup.string().trim().required("Account number is required.")
        })
      )
    })
  )

});

const ContractOrderCRUD = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const formReference = useRef(null) as any;

  const { contractOrderKey } = useParams();

  const { appSetting, writeLog, userFunctionAccess, showAlert, isUserAuthenticated, referenceLookups } = useContext(AppContext);

  const [contractOrders, setContractOrders] = useState<ContractOrder[]>([]);
  const [accounts, setAccounts] = useState<any>([]);
  const [accountTreeOpen, setAccountTreeOpen] = useState<boolean>(false);
  const [addProgramsConfirmOpen, setAddProgramsConfirmOpen] = useState<boolean>(false);
  const [confirmationDialogSetting, setConfirmationDialogSetting] = useState<ConfirmationDialogProbs>(defaultConfirmationDialogSetting);
  const [deleteAccountConfirmOpen, setDeleteAccountConfirmOpen] = useState<boolean>(false);
  const [accountToDelete, setAccountToDelete] = useState<{ programIndex: number, account: any } | null>(null);
  const [dataChanged, setDataChanged] = useState<boolean>(false);

  // Manage Accounts Dialog State
  const [manageAccountsDialogOpen, setManageAccountsDialogOpen] = useState<boolean>(false);
  const [selectedProgramForManage, setSelectedProgramForManage] = useState<number>(-1);
  const [selectedAccountsForAction, setSelectedAccountsForAction] = useState<number[]>([]);
  const [actionType, setActionType] = useState<string>('');
  const [transferContractOrderKey, setTransferContractOrderKey] = useState<string>('');
  const [transferContractOrderKeyProgram, setTransferContractOrderKeyProgram] = useState<string>('');
  const [transferContractOrderPrograms, setTransferContractOrderPrograms] = useState<any[]>([]);
  const [manageAccountsConfirmOpen, setManageAccountsConfirmOpen] = useState<boolean>(false);
  const [manageAccountsSearchText, setManageAccountsSearchText] = useState<string>('');
  const [manageAccountsSortConfig, setManageAccountsSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: '', direction: 'asc' });
  const [manageAccountsPage, setManageAccountsPage] = useState<number>(0);
  const [manageAccountsRowsPerPage, setManageAccountsRowsPerPage] = useState<number>(10);
  const [pendingManageAccountsPayload, setPendingManageAccountsPayload] = useState<ManageAccountsPayload | null>(null);
  const [initialFormValues, setInitialFormValues] = useState<ContractOrder>(formDefaultValue);
  const [isNew, setIsNew] = useState<boolean>(true);
  const [limitedUpdates, setLimitedUpdates] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [lookupValues, setlookupValues] = useState<ContractOrderLookupValues>({ orderStatus: [], contractType: [], invoiceStatus: [], postingStatus: [], vendors: [] });
  const [openAccountSearchDialog, setOpenAccountSearchDialog] = useState<boolean>(false);
  const [openAccountEditDialog, setOpenAccountEditDialog] = useState<boolean>(false);
  const [editAccountNumber, setEditAccountNumber] = useState<string>("");
  const [preOrderSummary, setPreOrderSummary] = useState<TitleCountSummaryItem[]>([]);
  const [programAnchorEl, setProgramAnchorEl] = useState<null | HTMLElement>(null);
  const [programAnchorElIndex, setProgramAnchorElIndex] = useState<number | null>(null);
  const [programEditable, setProgramEditable] = useState<boolean>(false);
  const [programKey, setProgramKey] = useState<string>("");
  const [programsLookupValues, setProgramsLookupValues] = useState<LookupWithCategory[]>([]);
  const [readonly, setReadonly] = useState<boolean>(false);
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
  const [selectedAccountIndex, setSelectedAccountIndex] = useState<number>(-1);
  const [selectedProgramIndex, setSelectedProgramIndex] = useState<number>(-1);
  const [accountPages, setAccountPages] = useState<{ [key: number]: number }>({});
  const [sortConfig, setSortConfig] = useState<{ [key: number]: { key: string; direction: 'asc' | 'desc' } }>({});
  const [globalSearchText, setGlobalSearchText] = useState<string>('');
  const [globalShowInvalidAccounts, setGlobalShowInvalidAccounts] = useState<boolean>(false);
  const [globalShowDeactivatedAccounts, setGlobalShowDeactivatedAccounts] = useState<boolean>(false);
  const [activeSearchText, setActiveSearchText] = useState<string>('');
  const [activeShowInvalidAccounts, setActiveShowInvalidAccounts] = useState<boolean>(false);
  const [activeShowDeactivatedAccounts, setActiveShowDeactivatedAccounts] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string>('');

  let confirmationDialogSettingName = "";

  const ROWS_PER_PAGE = 10;

  // Pagination helper functions
  const getCurrentPage = (programIndex: number): number => {
    return accountPages[programIndex] || 0;
  };

  const setCurrentPage = (programIndex: number, page: number) => {
    setAccountPages(prev => ({ ...prev, [programIndex]: page }));
  };

  const getSearchText = (programIndex: number): string => {
    return activeSearchText;
  };

  const getShowInvalidAccounts = (programIndex: number): boolean => {
    return activeShowInvalidAccounts;
  };

  const getShowDeactivatedAccounts = (programIndex?: number): boolean => {
    return activeShowDeactivatedAccounts;
  };

  const handleSearch = () => {
    // Clear any previous errors
    setSearchError('');

    // Validate search text
    if (globalSearchText.length > 0 && globalSearchText.length < 4) {
      setSearchError('Search text must be at least 4 characters or empty');
      return;
    }

    // Apply the search
    setActiveSearchText(globalSearchText);
    setActiveShowInvalidAccounts(globalShowInvalidAccounts);
    setActiveShowDeactivatedAccounts(globalShowDeactivatedAccounts);

    // Reset all pagination
    setAccountPages({});
  };

  const handleClearSearch = () => {
    setGlobalSearchText('');
    setGlobalShowInvalidAccounts(false);
    setGlobalShowDeactivatedAccounts(false);
    setActiveSearchText('');
    setActiveShowInvalidAccounts(false);
    setActiveShowDeactivatedAccounts(false);
    setSearchError('');
    setAccountPages({});
  };

  const hasActiveFilters = (): boolean => {
    return activeSearchText !== '' || activeShowInvalidAccounts || activeShowDeactivatedAccounts;
  };

  const filterAccounts = (accounts: any[], programIndex: number) => {
    const searchTerm = getSearchText(programIndex).toLowerCase();
    const showInvalid = getShowInvalidAccounts(programIndex);
    const showDeactivated = getShowDeactivatedAccounts(programIndex);


    return accounts.filter(account => {
      // Text search filter
      const matchesSearch = !searchTerm ||
        (account.accountNumber && account.accountNumber.toLowerCase().includes(searchTerm)) ||
        (account.folderName && account.folderName.toLowerCase().includes(searchTerm)) ||
        (account.folderId && account.folderId.toString().includes(searchTerm));

      // Invalid accounts filter (folderId === -1 means "Not Found")
      const isInvalidAccount = account.folderId <= 0;
      const matchesInvalidFilter = !showInvalid || isInvalidAccount;

      // Deactivated accounts filter (active === false means deactivated)
      let matchesDeactivatedFilter = true;
      if (showDeactivated) {
        matchesDeactivatedFilter = account.active === false;
      }

      // Apply search and invalid filters only if there are active filters
      if (hasActiveFilters()) {
        return matchesSearch && matchesInvalidFilter && matchesDeactivatedFilter;
      } else {
        // If no other active filters, still apply the deactivated filter (hide by default)
        return matchesDeactivatedFilter;
      }
    });
  };

  const getPaginatedAccounts = (accounts: any[], programIndex: number) => {
    const filteredAccounts = filterAccounts(accounts, programIndex);
    const sortedAccounts = sortAccounts(filteredAccounts, programIndex);
    const currentPage = getCurrentPage(programIndex);
    const startIndex = currentPage * ROWS_PER_PAGE;
    const endIndex = startIndex + ROWS_PER_PAGE;
    return sortedAccounts.slice(startIndex, endIndex) as ContractOrderAccount[];
  };

  const getFilteredAccountsCount = (accounts: any[], programIndex: number): number => {
    return filterAccounts(accounts, programIndex).length;
  };

  const getTotalPages = (accounts: any[], programIndex?: number): number => {
    if (programIndex !== undefined) {
      const filteredCount = getFilteredAccountsCount(accounts, programIndex);
      return Math.ceil(filteredCount / ROWS_PER_PAGE);
    }
    return Math.ceil(accounts.length / ROWS_PER_PAGE);
  };

  const getInvalidAccountsCount = (accounts: any[]): number => {
    return accounts.filter(account => account.folderId <= 0).length;
  };

  const areAllProgramAccountsValid = (programs: any[]): boolean => {
    if (!programs || programs.length === 0) return false;

    for (const program of programs) {
      // If program has no accounts, it's valid
      if (!program.accounts || program.accounts.length === 0) {
        continue;
      }
      // If any account in this program has invalid folder ID, return false
      if (program.accounts.some(account => account.folderId <= 0)) {
        return false;
      }
    }

    // All programs are valid if none have invalid accounts
    return true;
  };

  const getTotalAccountsCount = (programs: any[]): number => {
    return programs.reduce((total, program) => total + (program.accounts ? program.accounts.length : 0), 0);
  };

  const getTotalInvalidAccountsCount = (programs: any[]): number => {
    return programs.reduce((total, program) => {
      if (program.accounts) {
        const invalidCount = program.accounts.filter((account: any) => account.folderId <= 0).length;
        return total + invalidCount;
      }
      return total;
    }, 0);
  };

  // Account number validation - allow alphanumeric, dash, and space only
  const isValidAccountNumber = (accountNumber: string): boolean => {
    if (!accountNumber || accountNumber.trim().length === 0) {
      return false;
    }
    // Regular expression to match only alphanumeric characters, dashes, and spaces
    const validAccountPattern = /^[a-zA-Z0-9\s-]+$/;
    return validAccountPattern.test(accountNumber.trim());
  };

  // Validate account numbers for ConfirmInput component
  const validateAccountNumbers = useCallback((input: string): string => {
    if (!input || input.trim().length === 0) {
      return "Account number is required.";
    }

    // If editing an existing account (selectedAccountIndex >= 0), check for comma and disallow it
    if (selectedAccountIndex >= 0) {
      if (input.includes(',')) {
        return "Commas are not allowed when editing a single account number.";
      }

      // For single account edit, validate the single account number
      const accountNumber = input.trim();

      if (!isValidAccountNumber(accountNumber)) {
        return "Invalid format: Only letters, numbers, spaces, and dashes are allowed.";
      }

      // Check for duplicates against other accounts in the same program
      const programs = formReference.current?.values?.programs as ContractOrderProgram[];
      const programAccounts = programs ? programs[selectedProgramIndex]?.accounts || [] : [];

      const isDuplicate = programAccounts.some((account, index) => {
        const isSameAccount = index === selectedAccountIndex;
        return !isSameAccount && account.accountNumber.toLowerCase().trim() === accountNumber.toLowerCase();
      });

      if (isDuplicate) {
        return "This account number already exists in the program.";
      }

      return ""; // No errors for single account edit
    }

    // For adding multiple accounts (selectedAccountIndex === -1)
    // Split by comma and clean up each account number
    const accountNumbers = input
      .split(',')
      .map(acc => acc.trim())
      .filter(acc => acc.length > 0);

    if (accountNumbers.length === 0) {
      return "Please enter at least one valid account number.";
    }

    // Get current program accounts for duplicate checking
    const programs = formReference.current?.values?.programs as ContractOrderProgram[];
    const programAccounts = programs ? programs[selectedProgramIndex]?.accounts || [] : [];

    // Check for validation issues
    const duplicates: string[] = [];
    const invalidFormat: string[] = [];
    const validAccounts: string[] = [];

    accountNumbers.forEach(accountNumber => {
      // First check if account number has valid format
      if (!isValidAccountNumber(accountNumber)) {
        invalidFormat.push(accountNumber);
        return;
      }

      // Check for duplicates against existing accounts
      const isDuplicate = programAccounts.some((account) => {
        return account.accountNumber.toLowerCase().trim() === accountNumber.toLowerCase().trim();
      });

      if (isDuplicate) {
        duplicates.push(accountNumber);
      } else {
        // Also check against other accounts being added in this batch
        const isDuplicateInBatch = validAccounts.some(validAcc =>
          validAcc.toLowerCase().trim() === accountNumber.toLowerCase().trim()
        );

        if (!isDuplicateInBatch) {
          validAccounts.push(accountNumber);
        } else {
          duplicates.push(accountNumber);
        }
      }
    });

    // Build error message
    const errors: string[] = [];
    if (invalidFormat.length > 0) {
      const invalidList = invalidFormat.join(', ');
      errors.push(`Invalid format: ${invalidList}`);
    }
    if (duplicates.length > 0) {
      const duplicateList = duplicates.join(', ');
      errors.push(`Already exist: ${duplicateList}`);
    }

    if (errors.length > 0) {
      return errors.join(' | ');
    }

    return ""; // No errors
  }, [selectedProgramIndex, selectedAccountIndex]);

  // Sorting helper functions
  const getCurrentSort = (programIndex: number) => {
    return sortConfig[programIndex] || { key: '', direction: 'asc' };
  };

  const handleSort = (programIndex: number, property: string) => {
    const currentSort = getCurrentSort(programIndex);
    const isAsc = currentSort.key === property && currentSort.direction === 'asc';
    setSortConfig(prev => ({
      ...prev,
      [programIndex]: { key: property, direction: isAsc ? 'desc' : 'asc' }
    }));
    // Reset to first page when sorting
    setCurrentPage(programIndex, 0);
  };

  const sortAccounts = (accounts: any[], programIndex: number) => {
    const currentSort = getCurrentSort(programIndex);
    if (!currentSort.key) return accounts;

    return [...accounts].sort((a, b) => {
      const aValue = a[currentSort.key] || '';
      const bValue = b[currentSort.key] || '';

      if (currentSort.direction === 'asc') {
        return aValue.toString().localeCompare(bValue.toString());
      } else {
        return bValue.toString().localeCompare(aValue.toString());
      }
    });
  };

  // Program input menu
  const isProgramMenuVisible = (program) => {

    // Hide menu when filters are active
    if (hasActiveFilters()) {
      return false;
    }
    return true;
    // const deleteMenuVisible = (!program.used && program.accounts.length === 0);
    // const addAccountMenuVisible = (((program.used && program.accounts.length > 0) ));

    // if (deleteMenuVisible || addAccountMenuVisible) {
    //   return true;
    // }
    // return false;
  }


  const handleProgramMenuOptionClick = (event: React.MouseEvent<HTMLElement>, index: number) => {
    setProgramAnchorEl(event.currentTarget);
    setProgramAnchorElIndex(index);
  };

  const handleProgramMenuOptionClose = () => {
    setProgramAnchorEl(null);
    setProgramAnchorElIndex(null);
  };

  // Program and account styled menu item 
  const StyledMenuItem = withStyles((theme) => ({
    root: {
      "&:focus": {
        backgroundColor: theme.palette.secondary.main,
        "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
          color: theme.palette.common.white,
        },
      },
    },
  }))(MenuItem);


  // Program and account tree path
  const openAccountTreeDialog = () => {

    var tempAccounts: { code: string; name: string; programCode: string }[] = [];

    if (formReference.current.values.programs.length > 0) {
      formReference.current.values.programs.forEach(item => {
        referenceLookups.programs.filter(program => program.programKey === item.programKey).forEach(program => {
          tempAccounts.push({ code: program.programCode, name: program.programName, programCode: program.programCode });
          item.accounts.forEach(account => {
            if (account.folderId <= 0) {
              tempAccounts.push({ code: account.accountNumber, name: account.accountNumber, programCode: item.programCode });
            }
            else {
              tempAccounts.push({ code: account.accountNumber, name: account.folderName + "(" + account.folderId + ")", programCode: formReference.current.values.programs[0].programCode });
            }
          })
        });
      });

    }
    else {
      referenceLookups.programs.filter(program => program.clientCompanyKey.toLowerCase() === formReference.current.values.clientCompanyKey.toLowerCase()).forEach(program => {
        tempAccounts.push({ code: program.programCode, name: program.programName, programCode: program.programCode })
      });
    }

    setAccounts(tempAccounts);
    setAccountTreeOpen(true);
  }

  const confirmAccountTreeDialog = (confirmOutput: ConfirmOutput) => {
    setAccountTreeOpen(false);
  }

  // User confirmation
  const openConfirmationDialog = (statusname) => {
    const tempConfirmationDialogSetting: ConfirmationDialogProbs = JSON.parse(JSON.stringify(defaultConfirmationDialogSetting));

    confirmationDialogSettingName = statusname;
    tempConfirmationDialogSetting.title = "CONFIRMATION";
    tempConfirmationDialogSetting.yesText = "CONFIRM";
    tempConfirmationDialogSetting.noText = "CANCEL";
    tempConfirmationDialogSetting.open = true;
    tempConfirmationDialogSetting.callbackOnClose = closeConfirmationDialog;
    tempConfirmationDialogSetting.callbackOnConfirm = resultConfirmationDialog;

    if (statusname === "available") {
      tempConfirmationDialogSetting.content = "Would you like to make the contract order 'available' for pre-order?<br/><br/>Note:<u style='color:red'>Before finalizing the contract order, please ensure that all attributes are reviewed and confirmed that they are accurate.</u>";
      setConfirmationDialogSetting(tempConfirmationDialogSetting);
    }
    else if (statusname === "pause") {
      tempConfirmationDialogSetting.content = "Would you like to 'pause' the contract order? <br/><br/>Note:<u style='color:red'>If you choose to pause the contract order, all pre-order records will be put on hold and cannot be processed until the contract order is made 'available' again.</u>";
      setConfirmationDialogSetting(tempConfirmationDialogSetting);
    }
    else if (statusname === "inactive") {
      tempConfirmationDialogSetting.yesText = "DELETE";

      if (formReference.current.values.orderCount > 0) {
        tempConfirmationDialogSetting.content = "Would you like to inactive this contract order? <br/><br/>Note:<u style='color:red'>Once inactive, the contract order will expire immediately and not be available for pre-order.</u>";
      }
      else {
        tempConfirmationDialogSetting.content = "Do you want to permanently delete this contract order?";
      }

      setConfirmationDialogSetting(tempConfirmationDialogSetting);
    }
  };

  const resultConfirmationDialog = async (confirm: boolean) => {
    if (confirm) {
      if (confirmationDialogSettingName === "available") {
        await updateStatus(confirmationDialogSettingName);
      } else if (confirmationDialogSettingName === "pause") {
        await updateStatus(confirmationDialogSettingName);
      } else if (confirmationDialogSettingName === "inactive") {
        await updateStatus(confirmationDialogSettingName);
      }
    }

    setConfirmationDialogSetting(defaultConfirmationDialogSetting);
    confirmationDialogSettingName = "";
  };

  const closeConfirmationDialog = () => {
    setConfirmationDialogSetting(defaultConfirmationDialogSetting);
    confirmationDialogSettingName = "";
  };

  // Account search and select confirmation
  const showAccountSearchDialog = (programIndex) => {
    setSelectedProgramIndex(programIndex);
    setSelectedAccountIndex(-1); // Set to -1 to indicate program-level search
    setProgramKey(formReference.current.values.programs[programIndex].programKey);
    setOpenAccountSearchDialog(true);
  }

  const confirmAccountSearchDialog = (confirmOutput: ConfirmOutput) => {
    setOpenAccountSearchDialog(false);
    if (confirmOutput.status === "select") {
      const programs = [...formReference.current.values.programs] as ContractOrderProgram[];
      const programAccounts = [...programs[selectedProgramIndex].accounts];

      // Split the comma-delimited account numbers
      const accountNumbers = confirmOutput.data
        .split(',')
        .map(acc => acc.trim())
        .filter(acc => acc.length > 0);

      // Get existing account numbers to check for duplicates
      const existingAccountNumbers = programAccounts.map(acc => acc.accountNumber.toLowerCase().trim());

      // Filter out duplicates and add new accounts
      const newAccounts: ContractOrderAccount[] = [];
      let newAccountNumberCount = 0;
      let existingAccountNumbersCount = 0;
      accountNumbers.forEach(accountNumber => {
        if (!existingAccountNumbers.includes(accountNumber.toLowerCase().trim())) {
          const newAccount = {
            accountNumber: accountNumber,
            folderId: 0,
            folderName: "",
            used: false,
            active:true
          };
          newAccounts.push(newAccount);
          existingAccountNumbers.push(accountNumber.toLowerCase().trim());
          newAccountNumberCount++;
        }
        else {
          existingAccountNumbersCount++;
        }
      });

      // Add new accounts to the beginning of the array
      const updatedAccounts = [...newAccounts, ...programAccounts];
      programs[selectedProgramIndex].accounts = updatedAccounts;

      formReference.current.setFieldValue("programs", programs);
      formReference.current.setFieldTouched('programs', true);
      formReference.current.validateForm();
      setCurrentPage(selectedProgramIndex, 0); // Reset to first page
      setSelectedAccountIndex(-1);
      setTimeout(() => { setDataChanged(true); }, 1000);

      showAlert({
        message: `Added ${newAccountNumberCount} new account(s) and found ${existingAccountNumbersCount} existing account(s).`, severity: 'info'
      });
    }
  }

  // Account edit dialog
  const showAccountEditDialog = (programIndex, accountIndex) => {
    setSelectedProgramIndex(programIndex);
    setSelectedAccountIndex(accountIndex);
    const currentAccountNumber = formReference.current.values.programs[programIndex].accounts[accountIndex].accountNumber;
    setEditAccountNumber(currentAccountNumber);
    setOpenAccountEditDialog(true);
  }

  const confirmAccountEditDialog = (confirmOutput: ConfirmOutput) => {
    setOpenAccountEditDialog(false);
    if (confirmOutput.status === "save") {
      if (selectedAccountIndex >= 0) {
        // Editing existing account - validation already happened in ConfirmInput
        const programs = formReference.current.values.programs as ContractOrderProgram[];

        programs[selectedProgramIndex].accounts[selectedAccountIndex].accountNumber = confirmOutput.data.trim();
        programs[selectedProgramIndex].accounts[selectedAccountIndex].folderId = 0;
        programs[selectedProgramIndex].accounts[selectedAccountIndex].folderName = "";
        formReference.current.setFieldValue("programs", programs);
        formReference.current.setFieldTouched('programs', true);
        formReference.current.validateForm();
        setSelectedAccountIndex(-1);
        setTimeout(() => { setDataChanged(true); }, 1000);
      } else if (selectedAccountIndex === -1) {
        // Adding new account(s) - validation already happened in ConfirmInput
        const programs = formReference.current.values.programs as ContractOrderProgram[];
        const programAccounts = programs[selectedProgramIndex].accounts;

        // Split by comma and clean up each account number
        const accountNumbers = confirmOutput.data
          .split(',')
          .map(acc => acc.trim())
          .filter(acc => acc.length > 0);

        // Add all valid accounts to the beginning of the array
        accountNumbers.forEach(accountNumber => {
          const newAccount : ContractOrderAccount = {
            accountNumber: accountNumber,
            folderId: 0,
            folderName: "",
            used: false,
            active: true
          };
          programAccounts.unshift(newAccount);
        });

        formReference.current.setFieldValue("programs", programs);
        formReference.current.setFieldTouched('programs', true);
        formReference.current.validateForm();
        setCurrentPage(selectedProgramIndex, 0); // Reset to first page
        setSelectedAccountIndex(-1);
        setDataChanged(true);
      }
    }
  }

  const showAddAccountDialog = (programIndex) => {
    setSelectedProgramIndex(programIndex);
    setSelectedAccountIndex(-1); // Use -1 to indicate adding new account
    setEditAccountNumber(""); // Start with empty account number
    setOpenAccountEditDialog(true);
  }

  // Manage accounts dialog handlers
  const showManageAccountsDialog = async (programIndex) => {
    setSelectedProgramForManage(programIndex);
    setSelectedAccountsForAction([]);
    setActionType('');
    setTransferContractOrderKey('');
    setManageAccountsDialogOpen(true);

    // Load contract orders for transfer dropdown
    await loadContractOrders();
  }

  const handleManageAccountsClose = () => {
    setManageAccountsDialogOpen(false);
    setSelectedProgramForManage(-1);
    setSelectedAccountsForAction([]);
    setActionType('');
    setTransferContractOrderKey('');
    setTransferContractOrderKeyProgram('');
    setTransferContractOrderPrograms([]);
    setManageAccountsSearchText('');
    setManageAccountsSortConfig({ key: '', direction: 'asc' });
    setManageAccountsPage(0);
    setPendingManageAccountsPayload(null);
  }

  const handleAccountSelection = (accountIndex: number) => {
    setSelectedAccountsForAction(prev => {
      if (prev.includes(accountIndex)) {
        return prev.filter(index => index !== accountIndex);
      } else {
        return [...prev, accountIndex];
      }
    });
  }

  const handleSelectAllAccounts = () => {
    if (selectedProgramForManage >= 0) {
      const allAccounts = formReference.current?.values?.programs?.[selectedProgramForManage]?.accounts || [];
      const allFilteredAccounts = getFilteredAndSortedAccounts(); // Get all filtered accounts across all pages
      const areAllSelected = allFilteredAccounts.length > 0 && allFilteredAccounts.every(filteredAccount => {
        const originalIndex = allAccounts.findIndex(account =>
          account.accountNumber === filteredAccount.accountNumber &&
          account.folderId === filteredAccount.folderId
        );
        return selectedAccountsForAction.includes(originalIndex);
      });

      if (areAllSelected) {
        // Unselect all accounts across all pages
        const allFilteredIndices = allFilteredAccounts.map(filteredAccount =>
          allAccounts.findIndex(account =>
            account.accountNumber === filteredAccount.accountNumber &&
            account.folderId === filteredAccount.folderId
          )
        ).filter(index => index !== -1);

        setSelectedAccountsForAction(prev =>
          prev.filter(index => !allFilteredIndices.includes(index))
        );
      } else {
        // Select all accounts across all pages
        const allFilteredIndices = allFilteredAccounts.map(filteredAccount =>
          allAccounts.findIndex(account =>
            account.accountNumber === filteredAccount.accountNumber &&
            account.folderId === filteredAccount.folderId
          )
        ).filter(index => index !== -1);

        setSelectedAccountsForAction(prev => {
          const newSelection = [...prev];
          allFilteredIndices.forEach(index => {
            if (!newSelection.includes(index)) {
              newSelection.push(index);
            }
          });
          return newSelection;
        });
      }
    }
  }

  const handleActionTypeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setActionType(event.target.value as string);
    if (event.target.value !== 'transfer') {
      setTransferContractOrderKey('');
      setTransferContractOrderKeyProgram('');
      setTransferContractOrderPrograms([]);
    }
  }

  const handleTransferContractOrderChange = async (event: React.ChangeEvent<{ value: unknown }>) => {
    const contractOrderKey = event.target.value as string;
    setTransferContractOrderKey(contractOrderKey);
    setTransferContractOrderKeyProgram(''); // Reset program selection

    if (contractOrderKey) {
      await loadTransferContractOrderPrograms(contractOrderKey);
    } else {
      setTransferContractOrderPrograms([]);
    }
  }

  const handleTransferContractOrderProgramChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setTransferContractOrderKeyProgram(event.target.value as string);
  }

  const handleManageAccountsSave = async () => {
    if (selectedAccountsForAction.length === 0) {
      showAlert({ message: "Please select at least one account.", severity: "warning" });
      return;
    }

    if (!actionType) {
      showAlert({ message: "Please select an action.", severity: "warning" });
      return;
    }

    if (actionType === 'transfer' && !transferContractOrderKey) {
      showAlert({ message: "Please select a contract order for transfer.", severity: "warning" });
      return;
    }

    if (actionType === 'transfer' && !transferContractOrderKeyProgram) {
      showAlert({ message: "Please select a program for transfer.", severity: "warning" });
      return;
    }

    // Build the payload
    const currentProgram = formReference.current?.values?.programs?.[selectedProgramForManage];
    if (!currentProgram) {
      showAlert({ message: "Invalid program selection.", severity: "error" });
      return;
    }

    // Get selected account numbers using the selected indices
    const selectedAccountNumbers = selectedAccountsForAction.map(accountIndex =>
      currentProgram.accounts[accountIndex]?.accountNumber
    ).filter(Boolean); // Remove any undefined values

    if (selectedAccountNumbers.length === 0) {
      showAlert({ message: "No valid accounts selected.", severity: "error" });
      return;
    }

    // Create the payload according to ManageAccountsPayload interface
    const payload: ManageAccountsPayload = {
      accountNumbers: selectedAccountNumbers,
      action: actionType as "deactivate" | "transfer",
      transferContractOrderKey: actionType === 'transfer' ? transferContractOrderKey : "",
      transferProgramKey: actionType === 'transfer' ? transferContractOrderKeyProgram : ""
    };

    // Store payload for use in confirmation handler
    setPendingManageAccountsPayload(payload);

    // Show confirmation dialog
    setManageAccountsConfirmOpen(true);
  }

  const handleManageAccountsConfirm = async (confirm: boolean) => {
    setManageAccountsConfirmOpen(false);

    if (!confirm) {
      // Clean up stored payload
      setPendingManageAccountsPayload(null);
      return;
    }

    // Get the stored payload and spread it to ensure immutability
    const payload: ManageAccountsPayload = { ...pendingManageAccountsPayload } as ManageAccountsPayload;

    if (!payload) {
      showAlert({ message: "Invalid payload data.", severity: "error" });
      return;
    }


    try {
      // Set loading state if you have one
      setSaveInProgress(true);

      // Get the program key from the selected program
      const currentProgram = formReference.current?.values?.programs?.[selectedProgramForManage];
      const programKey = currentProgram?.programKey;

      if (!programKey) {
        showAlert({ message: "Invalid program key.", severity: "error" });
        setSaveInProgress(false);
        return;
      }

      // Make API call with the payload including program key in URL
      await API.post(`/api/contractOrders/${contractOrderKey}/${programKey}/manageaccounts`, payload)
        .then((response) => {
          const actionText = payload.action === 'deactivate' ? 'deactivated' : 'transferred and deactivated';
          showAlert({
            message: `${payload.accountNumbers.length} account(s) have been ${actionText} successfully.`,
            severity: "success"
          });

          // Refresh the contract order data to reflect changes
          getData();
          handleManageAccountsClose();
        })
        .catch((error) => {
          setSaveInProgress(false);
          showAlert({
            message: apiReponseError(error, "Failed to manage accounts."),
            severity: "error"
          });
        });

    } catch (error) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(error, "Something went wrong while managing accounts."),
        severity: "error"
      });
    } finally {
      setSaveInProgress(false);
      // Clean up stored payload
      setPendingManageAccountsPayload(null);
    }
  }

  // Helper function to get selected contract order details
  const getSelectedTransferContractOrder = () => {
    return contractOrders.find(order => order.contractOrderKey === transferContractOrderKey);
  }

  // Helper function to get selected program details
  const getSelectedTransferProgram = () => {
    return transferContractOrderPrograms.find(program => program.programKey === transferContractOrderKeyProgram);
  }

  // Helper function to generate confirmation message
  const getConfirmationMessage = () => {
    const actionText = actionType === 'deactivate' ? 'deactivate' : 'transfer and deactivate';
    let message = `Are you sure you want to ${actionText} ${selectedAccountsForAction.length} selected account(s)?`;

    if (actionType === 'transfer') {
      const selectedContractOrder = getSelectedTransferContractOrder();
      const selectedProgram = getSelectedTransferProgram();

      if (selectedContractOrder && selectedProgram) {
        message += `<br/><br/><strong>Transfer Details:</strong>`;
        message += `<br/>• Contract Order: ${selectedContractOrder.contractOrderNumber}`;
        message += `<br/>• Program: ${selectedProgram.programCode} - ${selectedProgram.programName}`;
      }
    }

    message += '<br/><br/>This action cannot be undone.';
    return message;
  }

  // Manage Accounts Dialog Helper Functions
  const getFilteredAndSortedAccounts = () => {
    if (selectedProgramForManage < 0 || !formReference.current?.values?.programs?.[selectedProgramForManage]?.accounts) {
      return [];
    }

    const accounts = formReference.current.values.programs[selectedProgramForManage].accounts;

    // Filter: show only active accounts (assuming folderId > 0 means active)
    let filteredAccounts = accounts.filter(account => account.folderId > 0);

    // Search filter
    if (manageAccountsSearchText.trim()) {
      const searchTerm = manageAccountsSearchText.toLowerCase().trim();
      filteredAccounts = filteredAccounts.filter(account =>
        account.accountNumber.toLowerCase().includes(searchTerm) ||
        (account.folderName && account.folderName.toLowerCase().includes(searchTerm))
      );
    }

    // Sort
    if (manageAccountsSortConfig.key) {
      filteredAccounts.sort((a, b) => {
        const aValue = a[manageAccountsSortConfig.key] || '';
        const bValue = b[manageAccountsSortConfig.key] || '';

        const compareResult = aValue.toString().localeCompare(bValue.toString());
        return manageAccountsSortConfig.direction === 'asc' ? compareResult : -compareResult;
      });
    }

    return filteredAccounts;
  }

  const getManageAccountsPaginatedAccounts = () => {
    const allFilteredAccounts = getFilteredAndSortedAccounts();
    const startIndex = manageAccountsPage * manageAccountsRowsPerPage;
    const endIndex = startIndex + manageAccountsRowsPerPage;
    return allFilteredAccounts.slice(startIndex, endIndex);
  }

  const areAllCurrentPageAccountsSelected = () => {
    if (selectedProgramForManage < 0) return false;

    const allAccounts = formReference.current?.values?.programs?.[selectedProgramForManage]?.accounts || [];
    const allFilteredAccounts = getFilteredAndSortedAccounts(); // Check all filtered accounts across all pages

    if (allFilteredAccounts.length === 0) return false;

    return allFilteredAccounts.every(filteredAccount => {
      const originalIndex = allAccounts.findIndex(account =>
        account.accountNumber === filteredAccount.accountNumber &&
        account.folderId === filteredAccount.folderId
      );
      return selectedAccountsForAction.includes(originalIndex);
    });
  }

  const handleManageAccountsSort = (property: string) => {
    const isAsc = manageAccountsSortConfig.key === property && manageAccountsSortConfig.direction === 'asc';
    setManageAccountsSortConfig({ key: property, direction: isAsc ? 'desc' : 'asc' });

    // Reset selected accounts and pagination when sorting changes
    setSelectedAccountsForAction([]);
    setManageAccountsPage(0);
  }

  const handleManageAccountsSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setManageAccountsSearchText(event.target.value);

    // Reset selected accounts and pagination when search changes
    setSelectedAccountsForAction([]);
    setManageAccountsPage(0);
  }

  const handleManageAccountsChangePage = (event: unknown, newPage: number) => {
    setManageAccountsPage(newPage);
  }

  const handleManageAccountsChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setManageAccountsRowsPerPage(parseInt(event.target.value, 10));
    setManageAccountsPage(0);
  }

  // Delete account confirmation
  const showDeleteAccountDialog = (programIndex, account) => {
    // If folder ID is <= 0, delete immediately without confirmation
    if (account.folderId <= 0) {
      // Delete immediately without setting state or showing dialog
      deleteAccountDirectly(programIndex, account);
    } else {
      // Set state and show confirmation dialog for accounts with valid folder ID
      setAccountToDelete({ programIndex, account });
      setDeleteAccountConfirmOpen(true);
    }
  }

  // Direct deletion for invalid accounts (folderId <= 0)
  const deleteAccountDirectly = (programIndex: number, account: any) => {
    const programs = formReference.current.values.programs as ContractOrderProgram[];
    const programAccounts = [...programs[programIndex].accounts];
    const accountNumber = account.accountNumber;
    const folderId = account.folderId;

    // Find the actual index of this account in the original array
    const accountToDeleteIndex = programAccounts.findIndex(acc =>
      acc.accountNumber === account.accountNumber &&
      acc.folderId === account.folderId &&
      acc.folderName === account.folderName
    );

    if (accountToDeleteIndex !== -1) {
      programAccounts.splice(accountToDeleteIndex, 1);
      formReference.current.setFieldValue(`programs.${programIndex}.accounts`, programAccounts);
      formReference.current.setFieldTouched('programs', true);
      formReference.current.validateForm();
      setDataChanged(true);

      // Adjust pagination if needed
      const filteredCount = getFilteredAccountsCount(programAccounts, programIndex);
      const currentPage = getCurrentPage(programIndex);
      const maxPages = Math.ceil(filteredCount / ROWS_PER_PAGE);

      if (currentPage >= maxPages && maxPages > 0) {
        setCurrentPage(programIndex, maxPages - 1);
      }

      if (folderId === 0) {
        showAlert({ message: "Account (" + accountNumber + ") deleted successfully", severity: "info" });
      } else {
        showAlert({ message: "Account (" + accountNumber + ") marked for deletion.", severity: "info" });
      }
    }
  }

  const confirmDeleteAccount = (confirm: boolean) => {
    setDeleteAccountConfirmOpen(false);
    if (confirm && accountToDelete) {
      const { programIndex, account } = accountToDelete;
      deleteAccountDirectly(programIndex, account);
    }
    setAccountToDelete(null);
  }

  // Add program and select confirmation
  const confirmAddProgramsDialog = (confirmOutput: ConfirmOutput) => {
    setAddProgramsConfirmOpen(false);
    if (confirmOutput.status === "select") {

      var currentPrograms: ContractOrderProgram[] = formReference.current.values.programs;
      var selectedItems = confirmOutput.data as Lookup[];

      selectedItems.forEach(item => {
        var selectedPrograms = referenceLookups.programs.filter(program => program.programKey === item.value);
        if (selectedPrograms.length === 1) {
          if (currentPrograms.filter(program => program.programKey.toLowerCase() === item.value.toLowerCase()).length === 0) {
            currentPrograms.push({ clientCompanyName: "", programKey: selectedPrograms[0].programKey, programCode: Number(selectedPrograms[0].programCode), programName: selectedPrograms[0].programName, used: false, taxExempt: selectedPrograms[0].taxExempt, accounts: [] })
          }
        }
      });

      formReference.current.setFieldValue("programs", currentPrograms);
      // if (currentPrograms.length > 1) {
      //   formReference.current.setFieldValue("accountNumber", "");
      //   formReference.current.setFieldValue("accountPrefix", "");
      //   formReference.current.setFieldValue("accountName", "");
      // }

      setDataChanged(true);

      setTimeout(() => { formReference.current.validateForm(); }, 2000);

    }
  }

  const handleSelectPrograms = () => {
    const lookupValues: LookupWithCategory[] = [];

    if (referenceLookups.combinedCompanyDefaultId === formReference.current.values.clientCompanyKey) {
      referenceLookups.programs.forEach((program) => {
        lookupValues.push({
          name: (!program.taxExempt ? "TX" : "NT") + " - " + program.programCode + " - " + program.programName,
          value: program.programKey,
          category: String(referenceLookups.clientCompanies.find(clientCompany => clientCompany.clientCompanyKey.toLowerCase() === program.clientCompanyKey.toLowerCase())?.name),
          checked: false
        });
      });
    }
    else {
      referenceLookups.programs.filter(program => program.clientCompanyKey.toLowerCase() === formReference.current.values.clientCompanyKey.toLowerCase()).forEach((program) => {
        if (formReference.current.values.programs.filter(item => String(item.programCode) === program.programCode).length === 0) {
          lookupValues.push({
            name: (!program.taxExempt ? "TX" : "NT") + " - " + program.programCode + " - " + program.programName,
            value: program.programKey,
            category: "Default",
            checked: false
          });
        }
      });
    }

    setProgramsLookupValues(lookupValues.sort((a, b) => ((a.category + a.name).localeCompare(b.category + b.name))));
    setAddProgramsConfirmOpen(true);
  };

  // Validate to show add program or not
  const validProgramEditable = () => {

    setProgramEditable(false);

    try {
      if (!readonly || limitedUpdates) {

        if (formReference.current.values.orderCount > 0) {
          if (formReference.current.values.programs.length === 0) {
            setProgramEditable(false);
            return;
          }
        }

        setProgramEditable(true);
        return;
      }
    }
    catch { }
    setProgramEditable(false);
  }

  // Get contract order detail
  const getData = async () => {

    setPreOrderSummary([]);
    setLoading(true);
    setIsNew(true);
    setReadonly(true);
    setLimitedUpdates(false);

    if (contractOrderKey === referenceLookups.newRecordDefaultId) {
      setIsNew(true);
    }
    else {
      setIsNew(false);
    }

    const apiResponse = await getContractOrder(contractOrderKey);

    if (apiResponse.success) {

      if (apiResponse.data.contractOrder.effectiveDate === "0001-01-01T00:00:00") {
        apiResponse.data.contractOrder.effectiveDate = "";
      }

      setInitialFormValues(apiResponse.data.contractOrder);
      setlookupValues(apiResponse.data.lookupValues);
      setPreOrderSummary(apiResponse.data.preOrderSummary);

      if (!apiResponse.data.contractOrder.lockedIndicator) {
        setReadonly(false);
        setLoading(false);

        if (apiResponse.data.contractOrder.statusId === 4) {
          setReadonly(true);
          setLimitedUpdates(false);
        }
      }
      else {
        setLoading(false);

        if (apiResponse.data.contractOrder.statusId === 4) {
          setReadonly(true);
          setLimitedUpdates(false);
        }
        else {
          setReadonly(false);
          setLimitedUpdates(true);
        }
      }

      if (!canWriteContractOrder(userFunctionAccess)) {
        setReadonly(true);
        setLimitedUpdates(false);
      }

      setTimeout(() => {
        validProgramEditable();
        formReference.current.setErrors({});
      }, 500);


    }
    else {
      showAlert({ message: apiResponse.errorMessage, severity: "error" });
      setLoading(false);
      redirectRoutePage("contractorders");
      return;
    }
  };

  // Load contract orders for transfer dropdown
  const loadContractOrders = async () => {
    try {
      const response = await API.get('/api/contractOrders');
      if (response.data && response.data.contractOrders) {
        // Get current contract order programs for comparison
        const currentPrograms = formReference.current?.values?.programs || [];
        const currentProgramKeys = currentPrograms.map(program => program.programKey);

        // Filter contract orders that:
        // 1. Are not the current contract order
        // 2. Have programs
        // 3. Have at least one program that matches the current contract order programs
        const filteredOrders = response.data.contractOrders.filter(
          (order: ContractOrder) => {
            // Skip current contract order
            if (order.contractOrderKey === contractOrderKey) {
              return false;
            }

            // Must have programs
            if (!order.programs || order.programs.length === 0) {
              return false;
            }

            // Check if any program in this order matches current contract order programs
            const hasMatchingProgram = order.programs.some(program =>
              currentProgramKeys.includes(program.programKey)
            );

            return hasMatchingProgram;
          }
        );

        setContractOrders(filteredOrders);
      }
    } catch (error) {
      showAlert({
        message: 'Failed to load contract orders for transfer.',
        severity: 'error'
      });
    }
  };

  // Load programs for selected transfer contract order
  const loadTransferContractOrderPrograms = async (contractOrderKey: string) => {
    try {
      const response = await API.get(`/api/contractOrders/${contractOrderKey}`);
      if (response.data && response.data.contractOrder && response.data.contractOrder.programs) {
        // Get current contract order programs for comparison
        const currentPrograms = formReference.current?.values?.programs || [];
        const currentProgramKeys = currentPrograms.map(program => program.programKey);

        // Filter to only show programs that match the current contract order programs
        const matchingPrograms = response.data.contractOrder.programs.filter(
          program => currentProgramKeys.includes(program.programKey)
        );

        setTransferContractOrderPrograms(matchingPrograms);

        // If only one program is available, auto-select it
        if (matchingPrograms.length === 1) {
          setTransferContractOrderKeyProgram(matchingPrograms[0].programKey);
        } else {
          // Reset selection if multiple programs are available
          setTransferContractOrderKeyProgram('');
        }
      }
    } catch (error) {
      showAlert({
        message: 'Failed to load programs for selected contract order.',
        severity: 'error'
      });
      setTransferContractOrderPrograms([]);
    }
  };

  // Validate the show save/update button or not
  const saveButtonVisible = () => {
    if (!readonly && dataChanged) {
      if (isFormReferenceValid(formReference)) {
        if (isFormDataValid(formReference)) {
          if (formReference.current.values.clientCompanyKey.toLowerCase() === referenceLookups.combinedCompanyDefaultId) {
            if (formReference.current.values.programs.length < 2) {
              return false;
            }
          }
          return true;
        }
      }
    }
    return false;
  }

  // Save contract order
  const save = async (values: ContractOrder) => {

    setSaveInProgress(false);

    formReference.current.validateForm();
    if (!isFormDataValid(formReference)) {
      return false;
    }


    const tempPrograms: { programKey: string, accounts: any[] }[] = [];

    values.programs.forEach(program => {
      var accountNumbers: any[] = [];
      program.accounts.forEach(function (account) {
        accountNumbers.push({ accountNumber: account.accountNumber });
      });
      tempPrograms.push({ programKey: program.programKey, accounts: accountNumbers });
    });

    const payload = {
      contractOrder: {
        clientCompanyKey: values.clientCompanyKey,
        contractTypeId: Number(values.contractTypeId),
        effectiveDate: values.effectiveDate,
        equipmentVendorId: Number(values.equipmentVendorId),
        expirationDate: values.expirationDate,
        invoiceStatusId: Number(values.invoiceStatusId),
        longDescription: values.longDescription,
        masterAgreementLevel: values.masterAgreementLevel,
        orderNumber: values.orderNumber,
        postingStatusId: Number(values.postingStatusId),
        programs: tempPrograms,
        shortDescription: values.shortDescription,
        taxExempt: values.taxExempt,
        override: values.override,
        clientCompanyShortName: values.clientCompanyShortName
      }
    }

    setSaveInProgress(true);

    try {
      setSaveInProgress(true);
      await API.post("/api/contractOrders/" + contractOrderKey, payload)
        .then(async (response) => {
          showAlert({ message: "Successfully saved.", severity: "success" });

          if (response.data.contractOrderKey !== "" && response.data.contractOrderKey !== null) {
            redirectRoutePage("contractorders/" + response.data.contractOrderKey);
          }
          else {
            goToRoutePage("contractorders");
          }

          return;
        })
        .catch((error) => {
          setSaveInProgress(false);
          const errorList: ApiResponseErrorObject[] = getErrorObject(error, "contractOrder.");
          let isErrorAssignedAtFieldLevel = true;
          try {
            errorList.forEach(function (error) {
              formReference.current.setFieldError(error.title, error.message.join(" "));
            });
          } catch (e) {
            isErrorAssignedAtFieldLevel = false;
          }
          isErrorAssignedAtFieldLevel = false;
          if (errorList.length <= 0 || !isErrorAssignedAtFieldLevel) {
            showAlert({
              message: apiReponseError(error, "Failed to save contract order data."),
              severity: "error",
            });
          }
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }

  };

  // Update contract order status
  const updateStatus = async (statusName: string) => {

    const payload = {};

    setSaveInProgress(true);

    try {
      setSaveInProgress(true);
      await API.post("/api/contractOrders/" + contractOrderKey + "/" + statusName, payload)
        .then(async (response) => {

          if (confirmationDialogSettingName === "inactive" && formReference.current.values.orderCount === 0) {
            showAlert({ message: "Contract order successfully deleted.", severity: "success" });
            redirectRoutePage("contractorders");
          }
          else {
            showAlert({ message: "Status update successfully.", severity: "success" });
            redirectRoutePage("contractorders/" + contractOrderKey);
          }
          return;
        })
        .catch((error) => {
          setSaveInProgress(false);
          showAlert({
            message: apiReponseError(error, "Failed to save contract order status."),
            severity: "error",
          });
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }

  };

  useEffect(() => {
    writeLog("Locked", "", "", "");
    setWindowTitle("Contract Order");
    window.scrollTo(0, 0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const initializePage = async () => {
      checkUserAuthenticated(isUserAuthenticated);
      if (isUserAuthenticated) {
        setLoading(false);
      }

      if (!canReadContractOrder(userFunctionAccess)) {
        showAlert({ message: "Access Denied.", severity: "error" });
        goToRoutePage("home");
        return;
      }

      if (contractOrderKey === referenceLookups.newRecordDefaultId) {
        if (!canWriteContractOrder(userFunctionAccess)) {
          showAlert({ message: "Access Denied.", severity: "error" });
          goToRoutePage("home");
          return;
        }
      }

      getData();
    };

    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthenticated, userFunctionAccess.userPermissionAdmin]);

  return (
    <React.Fragment>
      {loading ? (
        <SplashWait />
      ) : (
        <Screen>

          <AccountTree accounts={accounts} open={accountTreeOpen}
            callActionButton={confirmAccountTreeDialog} />

          <AccountSearch programKey={programKey} open={openAccountSearchDialog && programKey !== ""} callActionButton={confirmAccountSearchDialog} />

          <ConfirmInput
            title={selectedAccountIndex >= 0 ? "Edit Account Number" : "Add Account Number(s)"}
            label={selectedAccountIndex >= 0 ? "Account Number" : "Account Number(s)"}
            content={selectedAccountIndex >= 0
              ? `<Box><Box>Instructions:</Box><ul><li>Enter a single account number.</li><li>Only letters, numbers, spaces, and dashes are allowed.</li><li>Duplicate account numbers will be rejected.</li></ul></Box>`
              : `<Box><Box>Instructions:</Box><ul><li>Enter one or more account numbers; use comma (,) to separate multiple account numbers.</li><li>Only letters, numbers, spaces, and dashes are allowed.</li><li>Duplicate account numbers will be rejected.</li></ul></Box>`
            }
            value={editAccountNumber}
            type="string"
            required={true}
            yesText="SAVE"
            noText="CANCEL"
            open={openAccountEditDialog}
            showDeleteButton={false}
            deleteConfirmationText=""
            callbackOnSave={confirmAccountEditDialog}
            customValidation={validateAccountNumbers}
          />

          <Confirm
            title={confirmationDialogSetting.title}
            content={confirmationDialogSetting.content}
            yesText={confirmationDialogSetting.yesText}
            noText={confirmationDialogSetting.noText}
            open={confirmationDialogSetting.open}
            callbackOnClose={confirmationDialogSetting.callbackOnClose}
            callbackOnConfirm={confirmationDialogSetting.callbackOnConfirm}
          />

          <Confirm
            title="Delete Account"
            content={`Are you sure you want to delete account "${accountToDelete?.account?.accountNumber || ''}"?<br/><br/>This action cannot be undone.`}
            yesText="DELETE"
            noText="CANCEL"
            open={deleteAccountConfirmOpen}
            callbackOnClose={() => confirmDeleteAccount(false)}
            callbackOnConfirm={confirmDeleteAccount}
          />

          <ConfirmSelect
            title="Select Programs"
            label={"Programs"}
            content={``}
            data={programsLookupValues}
            yesText="SELECT"
            noText="CANCEL"
            open={addProgramsConfirmOpen}
            multiSelect={true}
            maxSelect={0}
            callbackOnSelect={confirmAddProgramsDialog}
          />

          {/* Manage Accounts Dialog */}
          <Dialog
            open={manageAccountsDialogOpen}
            className={globalStyles.dialogContainer} maxWidth={"lg"} fullWidth={true}
          >
            <DialogTitle className={globalStyles.dialogTitleContainer}>
              <Typography variant="subtitle1" component="div">
                Manage Accounts - {selectedProgramForManage >= 0 && formReference.current?.values?.programs?.[selectedProgramForManage] ? formReference.current.values.programs[selectedProgramForManage].programName : ''}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Box mb={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                      <InputLabel shrink>Action</InputLabel>
                      <Select
                        value={actionType}
                        onChange={handleActionTypeChange}
                        displayEmpty
                      >
                        <MenuItem value="">Select Action</MenuItem>
                        <MenuItem value="deactivate">Deactivate Accounts</MenuItem>
                        <MenuItem value="transfer">Transfer to Different Contract Order</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {actionType === 'transfer' && (
                    <>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                          <InputLabel shrink>Contract Order</InputLabel>
                          <Select
                            value={transferContractOrderKey}
                            onChange={handleTransferContractOrderChange}
                            displayEmpty
                          >
                            <MenuItem value="">Select Contract Order</MenuItem>
                            {contractOrders.map((order, index) => (
                              <MenuItem key={index} value={order.contractOrderKey}>
                                {order.contractOrderNumber}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      {transferContractOrderKey && (
                        <Grid item xs={12} sm={4}>
                          <FormControl fullWidth>
                            <InputLabel shrink>Program</InputLabel>
                            <Select
                              value={transferContractOrderKeyProgram}
                              onChange={handleTransferContractOrderProgramChange}
                              displayEmpty
                            >
                              <MenuItem value="">Select Program</MenuItem>
                              {transferContractOrderPrograms.map((program, index) => (
                                <MenuItem key={index} value={program.programKey}>
                                  {program.programName}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      )}

                    </>
                  )}
                </Grid>
              </Box>

              <Box mb={2}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search Accounts"
                      placeholder="Search by account number or folder name"
                      value={manageAccountsSearchText}
                      onChange={handleManageAccountsSearch}
                    />
                  </Grid>
                </Grid>
              </Box>

              <TableContainer style={{ minHeight: 400, maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={areAllCurrentPageAccountsSelected()}
                          onChange={handleSelectAllAccounts}
                        />
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={manageAccountsSortConfig.key === 'accountNumber'}
                          direction={manageAccountsSortConfig.key === 'accountNumber' ? manageAccountsSortConfig.direction : 'asc'}
                          onClick={() => handleManageAccountsSort('accountNumber')}
                        >
                          Account Number
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={manageAccountsSortConfig.key === 'folderName'}
                          direction={manageAccountsSortConfig.key === 'folderName' ? manageAccountsSortConfig.direction : 'asc'}
                          onClick={() => handleManageAccountsSort('folderName')}
                        >
                          Folder Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={manageAccountsSortConfig.key === 'folderId'}
                          direction={manageAccountsSortConfig.key === 'folderId' ? manageAccountsSortConfig.direction : 'asc'}
                          onClick={() => handleManageAccountsSort('folderId')}
                        >
                          Folder ID
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Used</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getManageAccountsPaginatedAccounts().map((account, displayIndex) => {
                      // Find the original index of this account
                      const allAccounts = formReference.current?.values?.programs?.[selectedProgramForManage]?.accounts || [];
                      const originalIndex = allAccounts.findIndex(originalAccount =>
                        originalAccount.accountNumber === account.accountNumber &&
                        originalAccount.folderId === account.folderId
                      );

                      return (
                        <TableRow key={displayIndex}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedAccountsForAction.includes(originalIndex)}
                              onChange={() => handleAccountSelection(originalIndex)}
                            />
                          </TableCell>
                          <TableCell>{account.accountNumber}</TableCell>
                          <TableCell>{account.folderName || 'N/A'}</TableCell>
                          <TableCell>{account.folderId}</TableCell>
                          <TableCell>{account.used ? 'Yes' : 'No'}</TableCell>
                        </TableRow>
                      );
                    })}
                    {getFilteredAndSortedAccounts().length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" style={{ padding: '20px' }}>
                          <Typography variant="body2" color="textSecondary">
                            No active accounts found{manageAccountsSearchText ? ' matching your search' : ''}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <TablePagination
                component="div"
                count={getFilteredAndSortedAccounts().length}
                page={manageAccountsPage}
                onPageChange={handleManageAccountsChangePage}
                rowsPerPage={manageAccountsRowsPerPage}
                onRowsPerPageChange={handleManageAccountsChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />


            </DialogContent>
            <DialogActions style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              {/* Footer with account count - aligned left */}
              <Box>
                <Typography variant="body2" color="textSecondary">
                  {selectedAccountsForAction.length} of {getFilteredAndSortedAccounts().length} accounts selected
                </Typography>
              </Box>

              {/* Buttons - aligned right */}
              <Box>
                <Button
                  onClick={handleManageAccountsSave}
                  color="primary"
                  variant="contained"
                  disabled={selectedAccountsForAction.length === 0 || !actionType || (actionType === 'transfer' && (!transferContractOrderKey || !transferContractOrderKeyProgram))}
                  style={{ marginRight: '8px' }}
                >
                  Save
                </Button>
                <Button onClick={handleManageAccountsClose} color="secondary">
                  Cancel
                </Button>
              </Box>
            </DialogActions>
          </Dialog>

          {/* Manage Accounts Confirmation Dialog */}
          <Confirm
            title="Confirm Account Management Action"
            content={getConfirmationMessage()}
            yesText="CONFIRM"
            noText="CANCEL"
            open={manageAccountsConfirmOpen}
            callbackOnClose={() => handleManageAccountsConfirm(false)}
            callbackOnConfirm={handleManageAccountsConfirm}
          />

          <Box className={pageStyles.root}>
            <Grid container spacing={3}>
              <Grid item xs={9}>
                <Box className={globalStyles.bottomSpacer}>
                  <Breadcrumbs aria-label="breadcrumb">
                    <Link color="inherit" onClick={() => goToRoutePage("home")} className={globalStyles.link}>
                      Home
                    </Link>
                    <Link color="inherit" onClick={() => goToRoutePage("contractorders")} className={globalStyles.link}>
                      Contract Orders
                    </Link>
                    <Typography color="textPrimary">{isNew ? "New" : readonly ? "View" : "Edit"}</Typography>
                  </Breadcrumbs>
                </Box>
              </Grid>
            </Grid>

            <SaveBackdrop open={saveInProgress} />

            <Formik
              innerRef={formReference}
              initialValues={initialFormValues}
              validationSchema={formValidationSchema}
              validateOnMount={true}
              initialTouched={formDefaultTouchValue}
              validateOnBlur={true}
              validateOnChange={true}
              onSubmit={(values: ContractOrder) => {
                save(values);
              }}
            >
              {({ values, errors, handleChange, handleSubmit, touched, handleBlur, isSubmitting, setFieldValue, resetForm }) => (
                <Form
                  onChange={(e) => {
                    handleChange(e);
                    setDataChanged(true);
                  }}
                  onPaste={() => { setTimeout(() => { setDataChanged(true); }, 1000) }}
                >
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={12} lg={9} className={globalStyles.formikContainer}>
                      <Grid container spacing={3} >

                        {
                          limitedUpdates ? (
                            <>
                              <Grid item xs={12}>
                                <Alert severity="info">{"Please note that limited updates can be made to this contract order since " + initialFormValues.orderCount + " orders have already been processed."}</Alert>
                              </Grid>
                              <Grid item xs={12}>
                                <Box p={1}></Box>
                              </Grid>
                            </>
                          ) : (
                            <Grid item xs={12}>
                              <Box p={1}></Box>
                            </Grid>
                          )
                        }

                        {
                          !isNew ? <>
                            <Grid item xs={12} sm={8} md={6} lg={6}>
                              <FormControl className={globalStyles.formControl} fullWidth={true}>
                                <InputLabel shrink={true}>Contract Order Number</InputLabel>
                                <Input
                                  disabled={true}
                                  value={values.contractOrderNumber}
                                  endAdornment={
                                    <InputAdornment position="end">
                                      {!readonly && programEditable ?
                                        <Tooltip arrow title="Copy Contract Order Number">
                                          <IconButton onClick={() => {
                                            navigator.clipboard.writeText(values.contractOrderNumber);
                                            showAlert({
                                              message: "Copy to clipboard.",
                                              severity: "info",
                                            });
                                          }}>
                                            <CopyTextIcon fontSize='small' style={{ color: '#999999' }} />
                                          </IconButton>
                                        </Tooltip>
                                        : <></>
                                      }
                                    </InputAdornment>
                                  }
                                />
                              </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={2} md={3} lg={3}>
                              <TextField disabled={true} id="status" label="Status" value={values.status} />
                            </Grid>

                            <Grid item xs={12} sm={2} md={3} lg={3}>
                              <FormControl className={globalStyles.formControl} fullWidth={true}>
                                <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                                  Locked
                                </FormLabel>
                                <FormGroup>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        disabled={true}
                                        id="lockedIndicator"
                                        name="lockedIndicator"
                                        checked={values.lockedIndicator}
                                        onChange={(ev, checked) => {
                                          setFieldValue("lockedIndicator", checked);
                                        }}
                                      />
                                    }
                                    label={values.lockedIndicator ? "Yes" : "No"}
                                    labelPlacement="end"
                                  />
                                </FormGroup>
                              </FormControl>
                            </Grid>
                          </> :
                            null
                        }

                        <Grid item xs={12} sm={6} md={6} lg={6}>
                          <TextField required disabled={readonly} id="shortDescription" label="Short Description" value={values.shortDescription} onChange={handleChange} onBlur={handleBlur} error={getIn(errors, "shortDescription")} helperText={getIn(errors, "shortDescription")} />
                        </Grid>

                        <Grid item xs={12} sm={6} md={6} lg={6}>
                          <TextField disabled={readonly} id="longDescription" label="Long Description" value={values.longDescription} onChange={handleChange} onBlur={handleBlur} error={getIn(errors, "longDescription")} helperText={getIn(errors, "longDescription")} />
                        </Grid>

                        <Grid item xs={12}>
                          <Box className={globalStyles.topic}>
                            <Grid container>
                              <Grid item xs={8}>
                                Key Values
                              </Grid>
                              <Grid item xs={4}>
                              </Grid>
                            </Grid>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={8} md={8} lg={6}>
                          <FormControl required className={globalStyles.formControl} fullWidth={true} error={Boolean(errors.clientCompanyKey)}>
                            <InputLabel id="client-company-code-label" shrink={true}>
                              Client Company
                            </InputLabel>
                            <Select
                              disabled={readonly || limitedUpdates || values.programs.length > 0} native
                              labelId="client-company-key-label"
                              id="clientCompanyKey"
                              name="clientCompanyKey"
                              value={values.clientCompanyKey}
                              onChange={(e) => {

                                var filterCompanies = referenceLookups.clientCompanies.filter(clientCompany => clientCompany.clientCompanyKey.toLowerCase() === String(e.currentTarget.value).toLowerCase());
                                if (filterCompanies.length === 1) {
                                  formReference.current.setFieldValue("clientCompanyShortName", filterCompanies[0].shortName);
                                }
                                else {
                                  formReference.current.setFieldValue("clientCompanyShortName", "");
                                }

                                handleChange(e);

                                setTimeout(() => { formReference.current.validateForm(); }, 500)

                              }}
                              label="Client Company"
                              error={getIn(errors, "clientCompanyKey")}>
                              <option value=""></option>

                              <option value={referenceLookups.combinedCompanyDefaultId} style={{ color: 'blue' }}>Combined Companies/Programs</option>

                              {referenceLookups.clientCompanies.map((clientCompany, index) => (
                                <option value={clientCompany.clientCompanyKey} key={index}>
                                  {clientCompany.name}
                                </option>
                              ))}
                            </Select>
                            <FormHelperText error>{getIn(errors, "clientCompanyKey")}</FormHelperText>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4} md={4} lg={3}>
                          <TextField disabled={readonly || limitedUpdates} id="clientCompanyShortName" label="Contract Order Prefix" value={values.clientCompanyShortName} onChange={handleChange} onBlur={handleBlur} error={getIn(errors, "clientCompanyShortName")} helperText={getIn(errors, "clientCompanyShortName")} />
                        </Grid>

                        <Grid item xs={12} sm={4} md={4} lg={3}>
                          <FormControl className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "equipmentVendorId"))}>
                            <InputLabel id="equipmentVendorId-label" required shrink={true}>
                              Equipment Vendor
                            </InputLabel>
                            <Select disabled={readonly || limitedUpdates} native labelId="equipmentVendorId-label" id="equipmentVendorId" name="equipmentVendorId" value={values.equipmentVendorId} onChange={handleChange} label="Saves Type" error={Boolean(getIn(touched, "equipmentVendorId") && getIn(errors, "equipmentVendorId"))}>
                              <option value={0}></option>
                              {lookupValues.vendors.map((item, index) => (
                                <option value={item.value} key={index}>
                                  {item.text}
                                </option>
                              ))}
                            </Select>
                            <FormHelperText error>{getIn(errors, "equipmentVendorId")}</FormHelperText>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4} md={4} lg={3}>
                          <TextField required disabled={readonly || limitedUpdates} id="masterAgreementLevel" label="Master Agreement Level" value={values.masterAgreementLevel} onChange={handleChange} onBlur={handleBlur} error={getIn(errors, "masterAgreementLevel")} helperText={getIn(errors, "masterAgreementLevel")} />
                        </Grid>

                        <Grid item xs={12} sm={4} md={4} lg={3}>
                          <TextField required disabled={readonly || limitedUpdates} id="orderNumber" label="Order Number" value={values.orderNumber} onChange={handleChange} onBlur={handleBlur} error={getIn(errors, "orderNumber")} helperText={getIn(errors, "orderNumber")} />
                        </Grid>

                        <Grid item xs={12} sm={4} md={4} lg={3}>
                          <FormControl className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "postingStatusId"))}>
                            <InputLabel id="postingStatusId-label" required shrink={true}>
                              Posting Status
                            </InputLabel>
                            <Select disabled={readonly || limitedUpdates} native labelId="postingStatusId-label" id="postingStatusId" name="postingStatusId" value={values.postingStatusId} onChange={handleChange} label="Saves Type" error={Boolean(getIn(touched, "postingStatusId") && getIn(errors, "postingStatusId"))}>
                              <option value={0}></option>
                              {lookupValues.postingStatus.map((item, index) => (
                                <option value={item.value} key={index}>
                                  {item.text}
                                </option>
                              ))}
                            </Select>
                            <FormHelperText error>{getIn(errors, "postingStatusId")}</FormHelperText>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4} md={4} lg={3}>
                          <FormControl className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "contractTypeId"))}>
                            <InputLabel required id="contractTypeId-label" shrink={true}>
                              Contract Type
                            </InputLabel>
                            <Select disabled={readonly || limitedUpdates} native labelId="contractTypeId-label" id="contractTypeId" name="contractTypeId" value={values.contractTypeId} onChange={handleChange} label="Saves Type" error={Boolean(getIn(touched, "contractTypeId") && getIn(errors, "contractTypeId"))}>
                              <option value={0}></option>
                              {lookupValues.contractType.map((item, index) => (
                                <option value={item.value} key={index}>
                                  {item.text}
                                </option>
                              ))}
                            </Select>
                            <FormHelperText error>{getIn(errors, "contractTypeId")}</FormHelperText>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4} md={4} lg={3}>
                          <FormControl className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "invoiceStatusId"))}>
                            <InputLabel id="invoiceStatusId-label" required shrink={true}>
                              Invoice Status
                            </InputLabel>
                            <Select disabled={readonly || limitedUpdates} native labelId="invoiceStatusId-label" id="invoiceStatusId" name="invoiceStatusId" value={values.invoiceStatusId} onChange={handleChange} label="Saves Type" error={Boolean(getIn(touched, "invoiceStatusId") && getIn(errors, "invoiceStatusId"))}>
                              <option value={0}></option>
                              {lookupValues.invoiceStatus.map((item, index) => (
                                <option value={item.value} key={index}>
                                  {item.text}
                                </option>
                              ))}
                            </Select>
                            <FormHelperText error>{getIn(errors, "invoiceStatusId")}</FormHelperText>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4} md={4} lg={3}>
                          <FormControl className={globalStyles.formControl} fullWidth={true}>
                            <FormLabel required component="legend" className={globalStyles.checkBoxLabel}>
                              Tax Exempt
                            </FormLabel>
                            <FormGroup>
                              <FormControlLabel
                                control={
                                  <Switch
                                    disabled={readonly || limitedUpdates}
                                    id="taxExempt"
                                    name="taxExempt"
                                    checked={values.taxExempt}
                                    onChange={(ev, checked) => {
                                      setFieldValue("taxExempt", checked);
                                    }}
                                  />
                                }
                                label={values.taxExempt ? "Yes" : "No"}
                                labelPlacement="end"
                              />
                            </FormGroup>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4} md={4} lg={3}>
                          <TextField disabled={readonly || limitedUpdates} id="override" label="Contract Order Suffix" value={values.override} onChange={handleChange} onBlur={handleBlur} error={getIn(errors, "override")} helperText={getIn(errors, "override")} />
                        </Grid>

                        <Grid item xs={12}></Grid>

                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <TextField required disabled={readonly || limitedUpdates} type='date' id="effectiveDate" label="Effective Date" defaultValue={formatDateForInput(values.effectiveDate)} onChange={handleChange} onBlur={handleBlur} error={Boolean(errors.effectiveDate)} helperText={errors.effectiveDate && touched.effectiveDate ? errors.effectiveDate : null} />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <TextField disabled={readonly} type='date' id="expirationDate" label="Expiration Date" defaultValue={formatDateForInput(values.expirationDate)} onChange={handleChange} onBlur={handleBlur} error={Boolean(errors.expirationDate)} helperText={errors.expirationDate && touched.expirationDate ? errors.expirationDate : null} />
                        </Grid>

                        <Grid item xs={12}>
                          <Box className={globalStyles.topic}>
                            <Grid container>
                              <Grid item xs={8}>
                                Associated Programs
                              </Grid>
                              <Grid item xs={4}>
                                <Box display="flex" justifyContent="flex-end" width={1}>
                                  {
                                    values.clientCompanyKey.trim() !== "" ?
                                      <Tooltip arrow title="Account Tree">
                                        <IconButton onClick={openAccountTreeDialog} >
                                          <AccountTreeIcon color="secondary" />
                                        </IconButton>
                                      </Tooltip>
                                      : null
                                  }

                                  {
                                    !readonly && programEditable && values.clientCompanyKey.trim() !== "" ? (
                                      <>
                                        &nbsp;
                                        &nbsp;
                                        <Tooltip arrow title="Add program">
                                          <IconButton onClick={handleSelectPrograms} >
                                            <AddIcon color="secondary" />
                                          </IconButton>
                                        </Tooltip>
                                      </>
                                    ) : null
                                  }
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        </Grid>

                        {
                          values.clientCompanyKey.toLowerCase() === referenceLookups.combinedCompanyDefaultId && values.programs.length < 2 ?
                            <Grid item xs={12}>
                              <Alert severity="info" >
                                The combined companies/programs option must include at least two programs from two different companies.
                              </Alert>
                            </Grid>
                            : null
                        }

                        {
                          values.programs.length > 0 && formReference?.current?.errors?.hasOwnProperty("accountItems") ?
                            <Grid item xs={12}>
                              <Alert severity="error"> {getIn(errors, "accountItems")}</Alert>
                            </Grid>
                            : null
                        }

                        {
                          values.programs.length > 0 ?
                            <Grid item xs={12}>
                              {
                                (values.programs.filter(program => program.taxExempt).length > 0 &&
                                  values.programs.filter(program => !program.taxExempt).length > 0) ?
                                  <>
                                    <Alert severity="warning" >
                                      This contract order is associated with both tax-exempt and non-tax exempt programs.
                                    </Alert>
                                    <Box p={1}>&nbsp;</Box>
                                  </>
                                  : (values.taxExempt && values.programs.filter(program => !program.taxExempt).length > 0) ?
                                    <>
                                      <Alert severity="warning" >
                                        This contract order is tax-exempt, while all programs are non-tax-exempt.
                                      </Alert>
                                      <Box p={1}>&nbsp;</Box>
                                    </>
                                    : (!values.taxExempt && values.programs.filter(program => program.taxExempt).length > 0) ?
                                      <>
                                        <Alert severity="warning" >
                                          This contract order is not tax-exempt, whereas all programs are tax-exempt.
                                        </Alert>
                                        <Box p={1}>&nbsp;</Box>
                                      </>
                                      : null
                              }

                              {/* Global Account Validation Status */}
                              <Alert severity={areAllProgramAccountsValid(values.programs) ? "info" : "error"}>
                                {areAllProgramAccountsValid(values.programs)
                                  ? `All accounts are ok (${getTotalAccountsCount(values.programs)} total accounts across all programs)`
                                  : `Some accounts need attention (${getTotalInvalidAccountsCount(values.programs)} invalid out of ${getTotalAccountsCount(values.programs)} total accounts across all programs)`
                                }
                              </Alert>
                            </Grid>
                            : null
                        }

                        {/* Global Search Controls for All Programs */}
                        {values.programs.length > 0 && (
                          <Grid item xs={12}>
                            <Box mb={2} p={2} style={{ backgroundColor: '#fff', borderRadius: '4px' }}>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={6} md={5} lg={5}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Search Text"
                                    placeholder="(min 4 chars)..."
                                    value={globalSearchText}
                                    onChange={(e) => {
                                      setGlobalSearchText(e.target.value);
                                      if (searchError) setSearchError('');
                                    }}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleSearch();
                                      }
                                    }}
                                    error={!!searchError}
                                    helperText={searchError}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={4}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={globalShowInvalidAccounts}
                                        onChange={(e) => {
                                          setGlobalShowInvalidAccounts(e.target.checked);
                                        }}
                                        size="small"
                                        color="primary"
                                      />
                                    }
                                    label="Show all invalid accounts"
                                  />
                                  <br />
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={globalShowDeactivatedAccounts}
                                        onChange={(e) => {
                                          setGlobalShowDeactivatedAccounts(e.target.checked);
                                        }}
                                        size="small"
                                        color="primary"
                                      />
                                    }
                                    label="Show all deactivated accounts"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={12} md={3} lg={3}>
                                  <Box display="flex">
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="primary"
                                      startIcon={<SearchIcon />}
                                      onClick={handleSearch}
                                      disabled={globalSearchText.length > 0 && globalSearchText.length < 4}
                                    >
                                      Search
                                    </Button>
                                    <Box ml={1}>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<ClearIcon />}
                                        onClick={handleClearSearch}
                                      >
                                        Clear
                                      </Button>
                                    </Box>
                                  </Box>
                                </Grid>
                              </Grid>
                              {hasActiveFilters() && (
                                <Box mt={1}>
                                  <Typography variant="caption" color="textSecondary">
                                    Active filters applied - showing filtered results across all programs
                                    {activeSearchText && ` matching "${activeSearchText}"`}
                                    {activeShowInvalidAccounts && ` (invalid accounts only)`}
                                    {activeShowDeactivatedAccounts && ` (deactivated accounts only)`}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Grid>
                        )}

                        {
                          values.programs.map((program, programIndex) => (
                            <>
                              {
                                programIndex > 0 ? <Grid item xs={12} key={programIndex}>&nbsp;</Grid> : null
                              }

                              <Grid item xs={12} key={programIndex} >
                                <Grid container spacing={3}>

                                  <Grid item xs={12} sm={12} md={12} lg={12}>
                                    <FormControl className={globalStyles.formControl} fullWidth={true}>
                                      <InputLabel shrink={true}>
                                        {
                                          values.clientCompanyKey.toLowerCase() === referenceLookups.combinedCompanyDefaultId ?
                                            program.clientCompanyName : "Program"
                                        }
                                      </InputLabel>
                                      <Input
                                        disabled={true}
                                        value={`${!program.taxExempt ? "TX" : "NT"} - ${program.programCode} - ${program.programName}`}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        endAdornment={
                                          <InputAdornment position="end">
                                            {
                                              isProgramMenuVisible(program) ?
                                                <IconButton onClick={(event) => handleProgramMenuOptionClick(event, programIndex)}>
                                                  <MoreIcon />
                                                </IconButton>
                                                : null
                                            }

                                          </InputAdornment>
                                        }
                                      />
                                    </FormControl>

                                    {
                                      programAnchorElIndex === programIndex && (
                                        <Menu
                                          anchorEl={programAnchorEl}
                                          open={Boolean(programAnchorEl)}
                                          onClose={handleProgramMenuOptionClose}
                                        >
                                          {
                                            !readonly ?
                                              <>
                                                {
                                                  !program.used && program.accounts.length === 0 ?
                                                    <StyledMenuItem
                                                      onClick={(e) => {
                                                        const programs = values.programs;
                                                        programs.splice(programIndex, 1);
                                                        formReference.current.setFieldValue(`programs`, programs);
                                                        setDataChanged(true);
                                                        handleProgramMenuOptionClose();
                                                      }}
                                                    >
                                                      <ListItemIcon>
                                                        <DeleteIcon fontSize="small" className={globalStyles.error} />
                                                      </ListItemIcon>
                                                      <ListItemText primary={"Delete"} />
                                                    </StyledMenuItem>
                                                    : null
                                                }

                                                <StyledMenuItem
                                                  onClick={(e) => {
                                                    showAccountSearchDialog(programIndex);
                                                    handleProgramMenuOptionClose();
                                                  }}
                                                >
                                                  <ListItemIcon>
                                                    <SearchIcon fontSize="small" />
                                                  </ListItemIcon>
                                                  <ListItemText primary={"Search Accounts (Meshify)"} />
                                                </StyledMenuItem>
                                                <StyledMenuItem
                                                  onClick={(e) => {
                                                    showAddAccountDialog(programIndex);
                                                    handleProgramMenuOptionClose();
                                                  }}
                                                >
                                                  <ListItemIcon>
                                                    <AddIcon fontSize="small" />
                                                  </ListItemIcon>
                                                  <ListItemText primary={"Add Accounts"} />
                                                </StyledMenuItem>

                                                {
                                                  program.accounts.length > 0 &&
                                                  <StyledMenuItem
                                                    onClick={(e) => {
                                                      showManageAccountsDialog(programIndex);
                                                      handleProgramMenuOptionClose();
                                                    }}
                                                  >
                                                    <ListItemIcon>
                                                      <SettingIcon fontSize="small" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={"Manage Accounts"} />
                                                  </StyledMenuItem>
                                                }

                                              </>
                                              : null
                                          }
                                        </Menu>
                                      )
                                    }

                                  </Grid>

                                  <Grid item xs={12}>

                                    <Box className={pageStyles.programAccountsContent}>
                                      {/* {
                                        program.accounts.length === 0 ?
                                          <Box>
                                            <Typography gutterBottom variant="caption" component="h6">
                                              <b>
                                                {!program.used ? "Accounts" : "Orders are processed through a program level, which means that accounts cannot be linked."}
                                              </b>
                                            </Typography>
                                            <br />
                                          </Box>
                                          : null
                                      } */}

                                      {
                                        program.accounts.length === 0 ?
                                          <Typography variant='subtitle2'>
                                            {!program.used ? "No accounts associated." : ""}
                                          </Typography>
                                          : hasActiveFilters() && getFilteredAccountsCount(program.accounts, programIndex) === 0 ?
                                            <Typography variant='subtitle2'>
                                              No accounts match the current search criteria.
                                            </Typography>
                                            :
                                            getFilteredAccountsCount(program.accounts, programIndex) > 0 && (
                                              <>
                                                <TableContainer className={program.accounts.length <= 10 ? "" : pageStyles.accountsTableContainer}>
                                                  <Table size="small" stickyHeader>
                                                    <TableHead>
                                                      <TableRow>
                                                        <TableCell className={`${pageStyles.tableHeaderCell} ${pageStyles.accountNumberCell}`}>
                                                          <TableSortLabel
                                                            active={getCurrentSort(programIndex).key === 'accountNumber'}
                                                            direction={getCurrentSort(programIndex).key === 'accountNumber' ? getCurrentSort(programIndex).direction : 'asc'}
                                                            onClick={() => handleSort(programIndex, 'accountNumber')}
                                                          >
                                                            <Typography variant="subtitle2"  >
                                                              Account Number
                                                            </Typography>
                                                          </TableSortLabel>
                                                        </TableCell>
                                                        <TableCell className={`${pageStyles.tableHeaderCell} ${pageStyles.folderIdCell}`}>
                                                          <TableSortLabel
                                                            active={getCurrentSort(programIndex).key === 'folderId'}
                                                            direction={getCurrentSort(programIndex).key === 'folderId' ? getCurrentSort(programIndex).direction : 'asc'}
                                                            onClick={() => handleSort(programIndex, 'folderId')}
                                                          >
                                                            <Typography variant="subtitle2" >
                                                              Folder ID
                                                            </Typography>
                                                          </TableSortLabel>
                                                        </TableCell>
                                                        <TableCell className={`${pageStyles.tableHeaderCell} ${pageStyles.folderNameCell}`}>
                                                          <TableSortLabel
                                                            active={getCurrentSort(programIndex).key === 'folderName'}
                                                            direction={getCurrentSort(programIndex).key === 'folderName' ? getCurrentSort(programIndex).direction : 'asc'}
                                                            onClick={() => handleSort(programIndex, 'folderName')}
                                                          >
                                                            <Typography variant="subtitle2">
                                                              Folder Name
                                                            </Typography>
                                                          </TableSortLabel>
                                                        </TableCell>

                                                        <TableCell className={`${pageStyles.tableHeaderCell} ${pageStyles.actionsHeaderCell}`}>
                                                          <Typography variant="subtitle2"  >
                                                            Actions
                                                          </Typography>
                                                        </TableCell>
                                                      </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                      {getPaginatedAccounts(program.accounts, programIndex).map((account, accountIndex) => {
                                                        // Find the original index of this account in the original array for error display
                                                        const originalAccountIndex = program.accounts.findIndex(acc =>
                                                          acc.accountNumber === account.accountNumber &&
                                                          acc.folderId === account.folderId &&
                                                          acc.folderName === account.folderName
                                                        );
                                                        return (
                                                          <TableRow key={`${programIndex} - ${originalAccountIndex}`}>

                                                            <TableCell className={`${pageStyles.tableBodyCell} ${pageStyles.accountNumberCell}`}>
                                                              <Typography
                                                                variant="body2"
                                                                color={Boolean(getIn(errors, `programs.${programIndex}.accounts.${originalAccountIndex}.accountNumber`)) || account.folderId <= 0 ? "error" : "textPrimary"}
                                                                title={account.accountNumber || "No account number"}
                                                              >
                                                                {account.accountNumber || "No account number"}
                                                              </Typography>
                                                              {Boolean(getIn(errors, `programs.${programIndex}.accounts.${originalAccountIndex}.accountNumber`)) && (
                                                                <Typography variant="caption" color="error" component="div">
                                                                  {getIn(errors, `programs.${programIndex}.accounts.${originalAccountIndex}.accountNumber`)}
                                                                </Typography>
                                                              )}
                                                            </TableCell>
                                                            <TableCell className={`${pageStyles.tableBodyCell} ${pageStyles.folderIdCell}`}>
                                                              <Typography
                                                                variant="body2"
                                                                color={account.folderId <= 0 ? "error" : "textPrimary"}
                                                                title={account.folderId > 0 ? account.folderId.toString() : 'Invalid Folder ID'}
                                                              >
                                                                {account.folderId > 0 ? account.folderId : ''}
                                                              </Typography>
                                                            </TableCell>
                                                            <TableCell className={`${pageStyles.tableBodyCell} ${pageStyles.folderNameCell}`}>
                                                              <Typography
                                                                variant="body2"
                                                                color={account.folderId === -1 ? "error" : "textPrimary"}
                                                                title={account.folderName && account.folderName !== "" && account.folderName !== ""
                                                                  ? account.folderName
                                                                  : ""
                                                                }
                                                              >
                                                                {account.folderName && account.folderName !== "" && account.folderName !== ""
                                                                  ? account.folderName
                                                                  : ""
                                                                }

                                                              </Typography>
                                                            </TableCell>

                                                            <TableCell className={`${pageStyles.tableBodyCell} ${pageStyles.actionsCell}`}>
                                                              <Box display="flex" alignItems="center" justifyContent="flex-end">
                                                                {!readonly && !account.used && account.folderId <= 0 && (
                                                                  <>
                                                                    <Tooltip title="Edit account number">
                                                                      <IconButton
                                                                        size="small"
                                                                        onClick={() => {
                                                                          // Find the actual index of this account in the original array
                                                                          const originalIndex = program.accounts.findIndex(acc =>
                                                                            acc.accountNumber === account.accountNumber &&
                                                                            acc.folderId === account.folderId &&
                                                                            acc.folderName === account.folderName
                                                                          );
                                                                          showAccountEditDialog(programIndex, originalIndex);
                                                                        }}
                                                                      >
                                                                        <EditIcon fontSize="small" />
                                                                      </IconButton>
                                                                    </Tooltip>
                                                                  </>
                                                                )}
 
                                                                {
                                                                  account.active === false ?
                                                                    account.used ?
                                                                      <Tooltip title="Account already used in orders and now deactivated">
                                                                        <ContactOrderAccountOffIcon fontSize="small" style={{ color: '#c0c0c0' }} />
                                                                      </Tooltip>
                                                                      :
                                                                      <Tooltip title="Deactivated">
                                                                        <ContactOrderAccountDisabledIcon fontSize="small" style={{ color: '#c0c0c0' }} />
                                                                      </Tooltip>

                                                                    : !readonly && !account.used   ? (
                                                                      <Tooltip title="Delete account">
                                                                        <IconButton
                                                                          size="small"
                                                                          onClick={() => {
                                                                            showDeleteAccountDialog(programIndex, account);
                                                                          }}
                                                                        >
                                                                          <DeleteIcon fontSize="small" color="error" />
                                                                        </IconButton>
                                                                      </Tooltip>
                                                                    ) : account.used ? (
                                                                      <Tooltip title="Account already used in orders and cannot be deleted">
                                                                        <ContactOrderAccountOnIcon fontSize="small" style={{ color: '#c0c0c0' }} />
                                                                      </Tooltip>
                                                                    ) : null
                                                                }

                                                                {
                                                                  account.folderId > 0 && (
                                                                    <Tooltip title="Go to Meshify Dashboard">
                                                                      <IconButton
                                                                        size="small"
                                                                        onClick={() => openExternalLinkWithTarget(appSetting.meshifyUrl + "/dashboard3#folders?folderId=" + account.folderId, "meshifyUrl")}
                                                                      >
                                                                        <OpenInNewIcon fontSize="small" />
                                                                      </IconButton>
                                                                    </Tooltip>
                                                                  )}
                                                              </Box>
                                                            </TableCell>
                                                          </TableRow>
                                                        );
                                                      })}
                                                    </TableBody>
                                                  </Table>
                                                </TableContainer>

                                                {/* Account Count and Pagination Controls */}
                                                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} className={pageStyles.paginationContainer}>
                                                  <Typography variant="body2" color="textSecondary">
                                                    {hasActiveFilters() ? (
                                                      <>
                                                        Showing {getFilteredAccountsCount(program.accounts, programIndex)} of {program.accounts.length} accounts
                                                        {activeSearchText && ` matching "${activeSearchText}"`}
                                                        {activeShowInvalidAccounts && ` (invalid accounts only)`}
                                                        {getInvalidAccountsCount(program.accounts) > 0 && (
                                                          <span style={{ color: '#f44336', marginLeft: '8px' }}>
                                                            ({getInvalidAccountsCount(program.accounts)} invalid)
                                                          </span>
                                                        )}
                                                      </>
                                                    ) : (
                                                      <>
                                                        Total accounts: {program.accounts.length}
                                                        {getInvalidAccountsCount(program.accounts) > 0 && (
                                                          <span style={{ color: '#f44336', marginLeft: '8px' }}>
                                                            ({getInvalidAccountsCount(program.accounts)} invalid)
                                                          </span>
                                                        )}
                                                      </>
                                                    )}
                                                  </Typography>

                                                  {getFilteredAccountsCount(program.accounts, programIndex) > ROWS_PER_PAGE && (
                                                    <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
                                                      <IconButton
                                                        size="small"
                                                        disabled={getCurrentPage(programIndex) === 0}
                                                        onClick={() => setCurrentPage(programIndex, getCurrentPage(programIndex) - 1)}
                                                      >
                                                        <ChevronLeftIcon fontSize="small" />
                                                      </IconButton>

                                                      <Typography variant="body2" style={{ margin: '0 4px' }}>
                                                        Page
                                                      </Typography>

                                                      <FormControl size="small" style={{ minWidth: '70px', backgroundColor: '#ffffff' }}>
                                                        <Select
                                                          native
                                                          value={getCurrentPage(programIndex) + 1}
                                                          onChange={(e) => setCurrentPage(programIndex, Number(e.target.value) - 1)}
                                                          style={{ fontSize: '0.875rem' }}
                                                        >
                                                          {Array.from({ length: getTotalPages(program.accounts, programIndex) }, (_, i) => (
                                                            <option key={i + 1} value={i + 1}>
                                                              &nbsp;&nbsp;{i + 1}
                                                            </option>
                                                          ))}
                                                        </Select>
                                                      </FormControl>

                                                      <Typography variant="body2" style={{ margin: '0 4px' }}>
                                                        of {getTotalPages(program.accounts, programIndex)}
                                                      </Typography>

                                                      <IconButton
                                                        size="small"
                                                        disabled={getCurrentPage(programIndex) >= getTotalPages(program.accounts, programIndex) - 1}
                                                        onClick={() => setCurrentPage(programIndex, getCurrentPage(programIndex) + 1)}
                                                      >
                                                        <ChevronRightIcon fontSize="small" />
                                                      </IconButton>
                                                    </Box>
                                                  )}
                                                </Box>
                                              </>
                                            )
                                      }
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </>
                          ))
                        }


                      </Grid>
                      <Box p={5}></Box>

                      <Box p={3} display="flex" justifyContent="center" alignContent="center">

                        {
                          !readonly && !isNew ?
                            <>
                              {
                                !dataChanged ?
                                  <Tooltip title="Delete this contract order.">
                                    <Button className={globalStyles.errorText} onClick={() => { openConfirmationDialog("inactive") }}                                    >
                                      <DeleteIcon />
                                    </Button>
                                  </Tooltip>
                                  : null
                              }

                              {
                                !dataChanged && (values.statusId === 2) ?
                                  <Tooltip title="If you need to temporarily pause a pre-order due to business reasons, click the 'Pause' button.">
                                    <Button id="button-available" className={pageStyles.pauseButton} variant="contained" onClick={() => openConfirmationDialog("pause")}>
                                      Set Pause
                                    </Button>
                                  </Tooltip>
                                  : null
                              }

                              {
                                !dataChanged && (values.statusId === 1 || values.statusId === 3) ?
                                  <Tooltip title="To place a pre-order using this contract order, simply click on the 'Available' button.">
                                    <Button id="button-available" className={pageStyles.availableButton} variant="contained" onClick={() => openConfirmationDialog("available")}>
                                      Set Available
                                    </Button>
                                  </Tooltip>
                                  : null
                              }
                            </>
                            : null
                        }

                        {saveButtonVisible() ? (
                          <Button
                            id="button-save"
                            color="secondary"
                            className={globalStyles.buttonDefault}
                            onClick={() => {
                              formReference.current.handleSubmit();
                            }}
                            variant="contained"
                          >
                            {isNew ? "Save" : "Update"}
                          </Button>
                        ) : null}

                        <Button id="button-cancel" color="default" className={globalStyles.buttonDefault} onClick={() => goToRoutePage("contractorders")}>
                          <u>{!readonly ? "Cancel" : "Back"}</u>
                        </Button>
                      </Box>

                      <Box p={2}></Box>
                    </Grid>
                    <Grid item xs={12} sm={12} lg={3}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={6} lg={12}>
                          <Card elevation={0} className={globalStyles.rightCardContainer}>
                            <CardHeader title="" subheader="ORDER DASHBOARD" />
                            <CardContent className={pageStyles.dashboardCardContent}>
                              <Grid container>
                                {
                                  preOrderSummary.map((item, index) => (
                                    <Grid item xs={12} key={index}>
                                      <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center">
                                        <Box>
                                          <Typography variant="h6" align="center" className="count">
                                            {item.count}
                                          </Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                            {item.title}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Grid>
                                  ))
                                }
                                <Grid item xs={12}>
                                  <Box p={3}></Box>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                          <Box p="3">&nbsp;</Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={6} lg={12}>
                          <Card elevation={0} className={globalStyles.rightCardContainer}>
                            <CardHeader title="" subheader="AUDIT" />
                            <CardContent className={pageStyles.dashboardCardContent}>
                              {
                                !isNew ?
                                  <Grid container>

                                    <Grid item xs={12}>
                                      <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center">
                                        <Box>
                                          <Typography variant="h6" className="text"  >
                                            {initialFormValues.createdBy !== "" ? initialFormValues.createdBy : ""}
                                          </Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                            Created By
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Grid>

                                    <Grid item xs={12}>
                                      <Box p={2}></Box>
                                    </Grid>

                                    <Grid item xs={12} >
                                      <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center">
                                        <Box>
                                          <Typography variant="h6" className="text"  >
                                            {initialFormValues.createdDate !== null ? <Moment date={initialFormValues.createdDate} format="MM/DD/YYYY hh:mm:ss" /> : ""}
                                          </Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                            Created On
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Grid>

                                    <Grid item xs={12}>
                                      <Box p={3}></Box>
                                    </Grid>

                                    <Grid item xs={12}>
                                      <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center">
                                        <Box>
                                          <Typography variant="h6" className="text"  >
                                            {initialFormValues.lastModifiedBy !== "" ? initialFormValues.lastModifiedBy : ""}
                                          </Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                            Last Update By
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Grid>


                                    <Grid item xs={12}>
                                      <Box p={2}></Box>
                                    </Grid>

                                    <Grid item xs={12}>
                                      <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center">
                                        <Box>
                                          <Typography variant="h6" className="text"  >
                                            {initialFormValues.lastModifiedDateTime !== null ? <Moment date={initialFormValues.lastModifiedDateTime} format="MM/DD/YYYY hh:mm:ss" /> : ""}
                                          </Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                            Last Update On
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Grid>
                                  </Grid>
                                  : null
                              }
                            </CardContent>
                          </Card>
                          <Box p="3">&nbsp;</Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>

                </Form>
              )}
            </Formik>
          </Box>
        </Screen>
      )}
    </React.Fragment>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 0,
    },
    countContainer: {
      justifyItems: "center",
    },
    pauseButton: {
      backgroundColor: theme.palette.info.dark,
      color: "#ffffff",
      marginLeft: "4px",
      marginRight: "4px",

      [theme.breakpoints.down("xs")]: {
        fontSize: 12,
      },
    },
    availableButton: {
      backgroundColor: theme.palette.success.main,
      color: "#ffffff",
      marginLeft: "4px",
      marginRight: "4px",

      [theme.breakpoints.down("xs")]: {
        fontSize: 12,
      },
    },
    programAccountsContent: {
      marginLeft: 0,
      paddingLeft: 5,
      paddingTop: 0,
      paddingBottom: 0,
      backgroundColor: theme.palette.type === "dark" ? theme.palette.grey[900] : "#fafafa",
    },
    // Table styles
    accountsTableContainer: {
      height: '550px',
      maxHeight: '550px',
      overflowY: 'auto',
    },
    tableHeaderCell: {
      padding: '8px',
    },
    tableBodyCell: {
      padding: '8px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    folderIdCell: {
      width: '120px',
    },
    folderNameCell: {
      minWidth: '200px',
    },
    accountNumberCell: {
      width: '200px',
    },
    actionsCell: {
      width: '150px',
      textAlign: 'right',
    },
    actionsHeaderCell: {
      width: '100px',
      textAlign: 'right',
    },
    paginationContainer: {
      backgroundColor: '#f2f0f0ff',
      paddingTop: '16px',
      paddingBottom: '16px',
      paddingRight: '8px',
      paddingLeft: '8px',
    },
    dashboardCardContent: {
      minHeight: "250px",
    },
    // Utility table classes
    tableContainer: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
    },
    tableHeaderText: {
      fontWeight: 'bold',
      color: theme.palette.text.primary,
    },
    tableCellEllipsis: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: 0, // This ensures ellipsis works with flexbox
    },
    actionButtonsContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: theme.spacing(0.5),
    },
  })
);

export default ContractOrderCRUD;
